import merge from 'lodash.merge';
import React, { Component, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { processSignLicenseStepLoad as sendAnalytics } from 'app/blocks/analytics/analytics';
import getPublicationStatus from 'app/blocks/article-publication-status/article-publication-status';
import { LinkButton } from 'app/blocks/buttons/buttons';
import ASInfo from 'app/blocks/common/as-info';
import { ID, l, withCodes2 } from 'app/blocks/common/codes';
import { processing, tryCatch, keyProps } from 'app/blocks/common/decorators';
import PageEnum from 'app/blocks/common/PageEnum';
import storage from 'app/blocks/common/storage';
import SvgIcon from 'app/blocks/common/svg-icon/svg-icon';
import { cloneDeepSimple } from 'app/blocks/common/utils';
import Layout from 'app/blocks/Layout/Layout';
import {
    getArticle,
    getDashboardUrl,
    getArticleEnrichments,
    ArticlePayload,
    navigateToDashboard,
} from 'app/blocks/middleware/dashboard';
import checkChanges from 'app/blocks/middleware/http/polling';
import StaticContent from 'app/blocks/static-content/static-content';
import { MainTitle } from 'app/blocks/titles/titles';
import BannerWidget from 'app/pages/article/widgets/BannerWidget';
import ArticleDetails from 'app/pages/dashboard/ArticleDetails';
import { isBlocked } from 'app/pages/dashboard/utils';
import ArticlePageContext from './ArticlePage.context';
import configure from './config';
import { SmallSkeleton, BigSkeleton, TopSkeleton } from './skeleton';
import convertItem from './utils';
import Citations from './widgets/Citations';
import ErrorWidget from './widgets/ErrorWidget';

import './ArticlePage.scss';

const TEMPORARY_BANNER_KEY = 'AS_31337_Temp_BANNER_key';

function BigLoader() {
    return (
        <main className="container-lg mt-3x" id="as-main-content-7f3d9e2a">
            <div>
                <LinkButton data-seleniumid="goToDashboardButton" onClick={() => navigateToDashboard()}>
                    <SvgIcon.previous iconTitle="Back Icon" />
                    {l('BUTTONS.BACK_TO_DASHBOARD')}
                </LinkButton>
            </div>

            <MainTitle data-seleniumid="article-page-title">{l('DASHBOARD.MANAGE_ARTICLE.TITLE')}</MainTitle>
            <Layout className="ArticlePage">
                <div style={{ overflow: 'hidden', textAlign: 'left' }}>
                    <TopSkeleton data-seleniumid="page-loading-skeleton" />
                </div>

                <div className="Content" data-seleniumid="article-page-content" style={{ marginTop: 0 }}>
                    <div className="Content-Left" data-seleniumid="left-column">
                        <BigSkeleton />
                    </div>
                    <div className="Content-Right">
                        <SmallSkeleton />
                        <SmallSkeleton />
                    </div>
                </div>
            </Layout>
        </main>
    );
}

type Props = {
    articleId: string;
    dashboardUrl?: string;
    isLoading: boolean;
    item: ArticlePayload;
    l: l;
};

const UiArticlePage = withCodes2(
    ID.DASHBOARD,
    ID.BUTTONS,
    ID.PANEL_STATUS,
    ID.WIDGETS,
    ID.LICENSE_SIGNING,
)(({ dashboardUrl, isLoading, item: rawItem }: Props): React.ReactElement => {
    const item = useMemo(() => convertItem(cloneDeepSimple(rawItem)), [rawItem]);
    const { important, left, required, right } = useMemo(() => configure(item), [item]);
    const { rcaInfo, status } = item;
    const { getAllRequiredActions, hasCitations, hasMultipleAuthors } = item.helpers;
    const statusCode = getPublicationStatus({ ...status, rcaInfo });

    useEffect(() => {
        const { article, history, journal, publication } = item;
        sendAnalytics({ article, history, journal, publication, statusCode });
    }, [item, statusCode]);

    const [isBannerVisible, setIsBannerVisible] = useState(
        hasMultipleAuthors() &&
            new Date().getTime() <
                parseInt(
                    storage.get('TEMPORARY_BANNER_HIDE_TIME_OVERRIDE') || l('LICENSE_SIGNING.BANNER_HIDE_TIME'),
                    10,
                ) &&
            !storage.get(TEMPORARY_BANNER_KEY),
    );

    function hideTemporaryBanner() {
        storage.set(TEMPORARY_BANNER_KEY, 'closed');
        setIsBannerVisible(false);
    }

    if (isBlocked(item)) {
        return <Navigate to="/index.html/dashboard" />;
    }

    const { article, journal, participantRole, publication } = item;

    function renderGrowImpactHeader() {
        return (
            <div className="Normal-Title" data-seleniumid="not-required-widgets-title">
                <MainTitle data-seleniumid="not-required-widgets-text">
                    {l('DASHBOARD.MANAGE_ARTICLE.GROW_IMPACT')}
                </MainTitle>
            </div>
        );
    }

    function renderGrowImpact(widgets) {
        return (
            <div className="Normal Widgets" data-seleniumid="not-required-widgets">
                {renderGrowImpactHeader()}
                {widgets}
            </div>
        );
    }

    return (
        <ArticlePageContext.Provider value={item}>
            <main className="container-lg mt-3x" id="as-main-content-7f3d9e2a">
                <div>
                    <LinkButton data-seleniumid="goToDashboardButton" href={dashboardUrl} isLinkTag>
                        <SvgIcon.previous iconTitle="Back Icon" />
                        {l('BUTTONS.BACK_TO_DASHBOARD')}
                    </LinkButton>
                </div>

                <MainTitle data-seleniumid="article-page-title">{l('DASHBOARD.MANAGE_ARTICLE.TITLE')}</MainTitle>
                <Layout className="ArticlePage">
                    <ArticleDetails item={item} statusCode={statusCode} />

                    <div className="Content" data-seleniumid="article-page-content">
                        <div className="Content-Left" data-seleniumid="left-column">
                            {!!item?.banner && Object.keys(item.banner).length > 0 && <BannerWidget />}
                            {!!item?.errors?.length && <ErrorWidget />}
                            {!!important.length && important}
                            {isBannerVisible && (
                                <div className="BannerContainer" data-seleniumid="banner-container">
                                    <SvgIcon.thunder className="BannerContainer-Icon" />
                                    <div className="BannerContainer-Text" data-seleniumid="banner-text">
                                        {l('LICENSE_SIGNING.BANNER_TEXT')}
                                    </div>
                                    <button
                                        className="BannerContainer-Button"
                                        data-seleniumid="dismiss-banner"
                                        onClick={hideTemporaryBanner}
                                        type="button"
                                    >
                                        <SvgIcon.cancel className="ButtonIcon" />
                                    </button>
                                </div>
                            )}
                            {!!required.length && (
                                <div className="Required Widgets" data-seleniumid="required-widgets">
                                    <div className="Required-Title" data-seleniumid="required-widgets-title">
                                        <SvgIcon.error seleniumid="required-widgets-icon" />
                                        <MainTitle data-seleniumid="required-widgets-text">
                                            {l('DASHBOARD.MANAGE_ARTICLE.REQUIRED_ACTIONS')}
                                        </MainTitle>
                                    </div>
                                    {required}
                                </div>
                            )}

                            {isLoading && (
                                <BigSkeleton data-seleniumid="content-loading-skeleton" style={{ marginTop: '30px' }} />
                            )}

                            {!isLoading && item?.status?.published && <Citations />}
                            {!isLoading && !!left.length && renderGrowImpact(left)}
                        </div>

                        {isLoading && (
                            <div className="Content-Right">
                                <SmallSkeleton />
                                <SmallSkeleton />
                            </div>
                        )}

                        {!isLoading && !!right.length && (
                            <div className="Content-Right" data-seleniumid="right-column">
                                {item?.status?.published && hasCitations() && renderGrowImpactHeader()}
                                {right}
                            </div>
                        )}
                    </div>
                </Layout>

                <StaticContent
                    className="mt-4x"
                    seleniumid="dashboard-promo-bottom"
                    src="dashboard-promo-bottom.html"
                />
            </main>
            <ASInfo
                article={article}
                journal={journal}
                other={{
                    fullWOAAOffer: !!(item.eligibility?.message && !item.eligibility?.isInstitutionalDiscount),
                    participantRole,
                    publication,
                    requiredActions: item.requiredActions,
                    requiredActionsList: getAllRequiredActions(),
                }}
                page={PageEnum.ARTICLE_PAGE}
            />
        </ArticlePageContext.Provider>
    );
});

type State = {
    enrichments: any;
    isLoading?: boolean;
    item: ArticlePayload;
    dashboardUrl?: string;
};

@keyProps('articleId')
class ArticlePage extends Component<{ articleId: string }, State> {
    state: State = {
        enrichments: null,
        isLoading: true,
        item: null,
    };

    polling = null;

    @processing('isLoading')
    @tryCatch.showPopUpAndGoToDashboard
    async componentDidMount() {
        const dashboardUrl = await getDashboardUrl();
        try {
            const { articleId } = this.props;
            const response = await getArticle(articleId);
            this.setState({ dashboardUrl, item: response.payload });
            this.polling = checkChanges(response);
            this.polling.watch(item => this.setState(state => ({ item: { ...merge(item, state.enrichments || {}) } })));

            await Promise.race([
                this.loadEnrichments(articleId, response.payload?.article?.doi),
                new Promise(r => setTimeout(r, 3000)),
            ]);
        } catch (error) {
            if (error.code === 'RESOURCE_NOT_FOUND') {
                navigateToDashboard();
            } else {
                throw error;
            }
        }
    }

    async loadEnrichments(articleId: string, doi: string) {
        let enrichments;
        try {
            enrichments = await getArticleEnrichments(articleId, doi);
        } catch (error) {
            enrichments = { enrichmentsError: 'WIDGETS.ERRORS.citations' };
        }

        this.setState(state => ({
            enrichments,
            item: { ...merge(state.item, enrichments) },
        }));
    }

    componentWillUnmount() {
        if (this.polling) {
            this.polling.stop();
        }
    }

    render() {
        const { articleId } = this.props;
        const { dashboardUrl, enrichments, isLoading, item } = this.state;

        if (isLoading || !item) {
            return <BigLoader />;
        }

        return <UiArticlePage articleId={articleId} dashboardUrl={dashboardUrl} isLoading={!enrichments} item={item} />;
    }
}

export { UiArticlePage };
export default ArticlePage;
